import {
  IconBuildings,
  IconChevronRight,
  IconLibrary,
  IconUsers,
  IconLockExclamation,
  IconCreditCard,
} from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizationDetails } from '../../reducers/authSlice';
import loadingGif from '../../assets/images/loader.gif';
import { useSubscription } from '../../contexts/SubscriptionContext';
import superAdminApiService from '../../services/super-admin-api.service';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import './OrganizationSettings.css';

function OrganisationSettings() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { orgId } = useParams(); // For Super Admin
  const { isActive: isSubscriptionActive } = useSubscription();

  const currentUserRole = useSelector(
    (state) => state.auth?.userAttributes['custom:role'],
  );
  const isSuperAdmin = currentUserRole === 'super_admin';
  const currentUserOrg = useSelector((state) => state.auth.organization);

  // State
  const [organizationData, setOrganizationData] = useState(null);
  const [isLoadingOrg, setIsLoadingOrg] = useState(isSuperAdmin);

  // Fetch organization data based on role
  useEffect(() => {
    const fetchOrgData = async () => {
      if (isSuperAdmin && orgId) {
        try {
          const org = await superAdminApiService.getOrgDetails(orgId);
          setOrganizationData(org);
        } catch (e) {
          console.error('Failed to fetch org details for super admin', e);
        }
      } else if (!isSuperAdmin) {
        setOrganizationData(currentUserOrg);
      }
      setIsLoadingOrg(false);
    };

    fetchOrgData();
  }, [isSuperAdmin, orgId, currentUserOrg]);

  // Refresh organization data function that can be called by children
  const refreshOrganizationData = async () => {
    if (isSuperAdmin && orgId) {
      try {
        const org = await superAdminApiService.getOrgDetails(orgId);
        setOrganizationData(org);
      } catch (e) {
        console.error('Failed to refresh org details for super admin', e);
      }
    } else if (!isSuperAdmin) {
      dispatch(fetchOrganizationDetails());
    }
  };

  // Context value to be passed to children
  const contextValue = {
    organizationData,
    isLoadingOrg,
    isSuperAdmin,
    orgId,
    refreshOrganizationData,
    isSubscriptionActive,
  };

  // Helper to render NavLink with optional tooltip for inactive subscription
  const renderNavLink = (
    to,
    icon,
    text,
    requiresActiveSubscription = false,
  ) => {
    const navLink = (
      <NavLink
        to={to}
        className={({ isActive }) =>
          `org-tab ${isActive ? 'active' : ''} ${
            !isSubscriptionActive && requiresActiveSubscription
              ? 'disabled'
              : ''
          }`
        }
        onClick={(e) => {
          if (!isSubscriptionActive && requiresActiveSubscription) {
            e.preventDefault();
          }
        }}
      >
        {icon}
        <span>{text}</span>
        {!isSubscriptionActive && requiresActiveSubscription && (
          <IconLockExclamation size={18} className="ms-1" />
        )}
      </NavLink>
    );

    if (!isSubscriptionActive && requiresActiveSubscription) {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="bottom"
          overlay={
            <Popover id={`popover-${text.replace(/\s+/g, '-').toLowerCase()}`}>
              <Popover.Header className="bg-light" as="h5">
                Subscription Required
              </Popover.Header>
              <Popover.Body>
                <div className="d-flex align-items-start">
                  <div>
                    This feature requires an active subscription. Please make
                    payment to activate your subscription to access{' '}
                    <strong>{text}</strong>.
                  </div>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <span className="d-inline-block">{navLink}</span>
        </OverlayTrigger>
      );
    }

    return navLink;
  };

  return (
    <>
      <div className="full-page-layout">
        <div className={`innerpage detailed-page org-settings-page px-3`}>
          {isSuperAdmin ? (
            <div className="d-flex align-items-center gap-2 mb-3">
              <h6 className="fs-18 fw-semibold mb-0">Organization</h6>
              <IconChevronRight size={18} />
              <h6 className="fs-18 fw-semibold mb-0 opacity-50">
                {organizationData?.org_name}
              </h6>
            </div>
          ) : (
            <div className="org-settings-header">
              <h1 className="org-settings-title">Organizational Settings</h1>
              <p className="org-settings-subtitle">
                Manage your organization's, users, and subscription details
              </p>
            </div>
          )}
          
          {!isLoadingOrg && (
            <div className="org-tabs">
              {renderNavLink(
                isSuperAdmin && orgId
                  ? `/super-admin/organizations/${orgId}/details`
                  : '/settings/organization/details',
                <IconBuildings size={20} />,
                'Organization Details',
                true, // Requires active subscription
              )}
              {renderNavLink(
                isSuperAdmin && orgId
                  ? `/super-admin/organizations/${orgId}/users`
                  : '/settings/organization/users',
                <IconUsers size={20} />,
                'User Management',
                true, // Requires active subscription
              )}
              {!organizationData?.sub_force_allow &&
                renderNavLink(
                isSuperAdmin && orgId
                  ? `/super-admin/organizations/${orgId}/billing`
                  : '/settings/organization/billing',
                <IconCreditCard size={20} />,
                'Subscription & Billing',
                false, // Always accessible
              )}
            </div>
          )}
          
          <div className="org-tab-content">
            {isLoadingOrg ? (
              <div className="text-center my-5">
                <img
                  width={140}
                  src={loadingGif}
                  alt="Loading..."
                  className="loader-gif"
                />
              </div>
            ) : (
              <Outlet context={contextValue} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default OrganisationSettings;
